import React, { useState, useEffect } from "react";
import { Users, FileText, ArrowUpRight, Zap } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { adminService } from "../services/adminService";

// ── Helpers ───────────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  "#4361EE", "#7C3AED", "#0EA5E9", "#10B981",
  "#F59E0B", "#EF4444", "#EC4899", "#14B8A6",
];
const getInitials  = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");
const getAvatarBg  = (name = "") => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};
const capitalize   = (s = "") => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
const formatTime   = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return isToday ? `Today · ${time}` : `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} · ${time}`;
};

const PLAN_COLORS = {
  individual:   "#F59E0B",
  professional: "#4361EE",
  enterprise:   "#7C3AED",
  business:     "#10B981",
  smb:          "#10B981",
};
const planBadgeClass = (tier = "") => {
  const t = tier.toLowerCase();
  if (t === "enterprise")   return "bg-purple-100 text-purple-700";
  if (t === "professional") return "bg-blue-100 text-blue-700";
  if (t === "business" || t === "smb") return "bg-teal-100 text-teal-700";
  return "bg-green-100 text-green-700";
};

const PlanBadge = ({ plan }) => (
  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap ${planBadgeClass(plan)}`}>
    {capitalize(plan)}
  </span>
);

// ── Sub-components ────────────────────────────────────────────────────────────
const KpiCard = ({ icon: Icon, iconBg, iconColor, value, label, delta }) => (
  <div className="bg-white border border-surface-border rounded-2xl p-5 flex flex-col gap-3 relative">
    <button className="absolute top-4 right-4 text-brand-light hover:text-brand-muted transition-colors">
      <ArrowUpRight className="w-4 h-4" />
    </button>
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg}`}>
      <Icon className={`w-5 h-5 ${iconColor}`} />
    </div>
    <div>
      <p className="text-3xl font-bold text-brand-text">{value}</p>
      <p className="text-sm text-brand-muted mt-0.5">{label}</p>
      {delta && <p className="text-xs font-medium text-emerald-500 mt-1">{delta}</p>}
    </div>
  </div>
);

const LatestSubscribed = ({ items }) => (
  <div className="bg-white border-2 border-dashed border-blue-400 rounded-2xl p-4 flex flex-col h-full">
    <div className="flex items-center justify-between mb-1 px-1">
      <div className="flex items-center gap-1.5">
        <Zap className="w-3.5 h-3.5 text-blue-500" />
        <span className="text-xs font-bold tracking-widest text-blue-600 uppercase">Latest Subscribed</span>
      </div>
      <span className="text-xs font-bold bg-green-100 text-green-600 px-2.5 py-0.5 rounded-full">New</span>
    </div>
    <p className="text-xs text-brand-muted px-1 mb-3">Recently subscribed customers</p>
    <div className="flex flex-col divide-y divide-dashed divide-blue-200">
      {items.map((c) => (
        <div key={c.id} className="flex flex-col py-3 px-1 gap-1">
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold"
              style={{ background: getAvatarBg(c.name) }}
            >
              {getInitials(c.name)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-brand-text truncate">{c.name}</p>
              <p className="text-xs text-brand-muted truncate">{c.email}</p>
            </div>
            <PlanBadge plan={c.plan_tier} />
          </div>
          <div className="flex items-center justify-between pl-10 text-xs text-brand-light">
            {["enterprise", "business", "smb", "professional"].includes((c.plan_tier || "").toLowerCase()) && c.company_name
              ? <span>{c.company_name}</span>
              : <span />}
            <span>{formatTime(c.created_at)}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const TopCustomers = ({ rows }) => (
  <div className="bg-white border border-surface-border rounded-2xl p-5 flex flex-col h-full">
    <p className="text-base font-bold text-brand-text mb-3">Top Analysis Customer</p>
    <div className="overflow-x-auto flex-1">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-surface-border">
            <th className="text-left pb-2 text-xs text-brand-light font-medium w-6"></th>
            <th className="text-left pb-2 text-xs text-brand-light font-medium">User</th>
            <th className="text-left pb-2 text-xs text-brand-light font-medium">Plan</th>
            <th className="text-right pb-2 text-xs text-brand-light font-medium">Analysis</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.rank} className="border-b border-surface-border last:border-0">
              <td className="py-3 text-xs text-brand-light font-medium">{r.rank}</td>
              <td className="py-3">
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold"
                    style={{ background: getAvatarBg(r.name) }}
                  >
                    {getInitials(r.name)}
                  </div>
                  <div>
                    <p className="font-semibold text-brand-text text-sm leading-tight">{r.name}</p>
                    <p className="text-xs text-brand-muted">{r.company_name || r.email}</p>
                  </div>
                </div>
              </td>
              <td className="py-3"><PlanBadge plan={r.plan_tier} /></td>
              <td className="py-3 text-right font-semibold text-brand-text">{r.analysisCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const DailyVolumeChart = ({ data, weeklyTotal }) => (
  <div className="bg-white border border-surface-border rounded-2xl p-5">
    <div className="flex items-start justify-between mb-4">
      <div>
        <p className="text-base font-bold text-brand-text">Daily Analysis Volume</p>
        <p className="text-xs text-brand-muted mt-0.5">Analysis run per day this week</p>
      </div>
      <span className="text-xs font-semibold bg-blue-50 text-blue-600 px-3 py-1 rounded-full">
        {weeklyTotal} this week
      </span>
    </div>
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }} barCategoryGap="35%">
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
        <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} tickCount={5} allowDecimals={false} />
        <Tooltip
          cursor={{ fill: "#F1F5F9" }}
          contentStyle={{ borderRadius: "8px", border: "1px solid #E2E8F0", fontSize: "12px" }}
          formatter={(val) => [val, "Analysis"]}
        />
        <Bar dataKey="count" fill="#4361EE" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const PlanDistributionChart = ({ data, total }) => (
  <div className="bg-white border border-surface-border rounded-2xl p-5 flex flex-col">
    <p className="text-base font-bold text-brand-text mb-0.5">Plan Distribution</p>
    <p className="text-xs text-brand-muted mb-4">Breakdown of active user subscription tiers</p>
    <div className="flex items-center gap-6 flex-1">
      <div className="relative flex-shrink-0" style={{ width: 180, height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={56}
              outerRadius={82}
              dataKey="count"
              startAngle={90}
              endAngle={-270}
              stroke="#fff"
              strokeWidth={2}
            >
              {data.map((entry, i) => (
                <Cell key={i} fill={PLAN_COLORS[entry.plan?.toLowerCase()] ?? "#CBD5E1"} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <p className="text-xs text-brand-muted font-medium">Total Users</p>
          <p className="text-xl font-bold text-brand-text">{total.toLocaleString()}</p>
        </div>
      </div>
      <div className="flex flex-col gap-3 flex-1">
        <div className="flex items-center justify-between text-xs text-brand-light font-medium pb-1 border-b border-surface-border">
          <span>Plan</span>
          <span>Users</span>
        </div>
        {data.map((item) => (
          <div key={item.plan} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ background: PLAN_COLORS[item.plan?.toLowerCase()] ?? "#CBD5E1" }} />
              <span className="text-xs text-brand-muted">{item.plan}</span>
            </div>
            <span className="text-sm font-semibold text-brand-text">{item.count.toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────
const AdminOverviewPage = () => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    let cancelled = false;
    adminService.getOverview()
      .then((res) => { if (!cancelled) setData(res?.data ?? res); })
      .catch((err) => { if (!cancelled) setError(err.message || "Failed to load overview."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const metrics            = data?.metrics ?? {};
  const totalUsers         = metrics.totalUsers           ?? 0;
  const totalAnalyses      = metrics.totalAnalyses        ?? 0;
  const newUsers           = metrics.newUsersThisMonth    ?? 0;
  const newAnalyses        = metrics.newAnalysesThisMonth ?? 0;
  const latestSubscribed   = data?.latestSubscribed       ?? [];
  const topCustomers       = data?.topAnalysisCustomers   ?? [];
  const dailyData          = data?.dailyAnalysisVolume?.data       ?? [];
  const weeklyTotal        = data?.dailyAnalysisVolume?.thisWeekTotal ?? 0;
  const planData           = data?.planDistribution?.data          ?? [];
  const planTotal          = data?.planDistribution?.totalUsers    ?? 0;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-bold text-brand-text">Admin Overview</h1>
        <p className="text-sm text-brand-muted mt-0.5">Platform-wide metrics and system health.</p>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-100 text-sm text-red-600 rounded-xl">{error}</div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {loading ? (
          [0, 1].map((i) => (
            <div key={i} className="bg-white border border-surface-border rounded-2xl p-5 animate-pulse flex flex-col gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-200" />
              <div className="h-8 w-28 bg-slate-200 rounded" />
              <div className="h-3 w-20 bg-slate-100 rounded" />
            </div>
          ))
        ) : (
          <>
            <KpiCard icon={Users}    iconBg="bg-blue-100"   iconColor="text-blue-500"   value={totalUsers.toLocaleString()}    label="Total Users"    delta={newUsers    ? `+${newUsers} this month`    : undefined} />
            <KpiCard icon={FileText} iconBg="bg-orange-100" iconColor="text-orange-500" value={totalAnalyses.toLocaleString()} label="Total Analysis" delta={newAnalyses ? `+${newAnalyses} this month` : undefined} />
          </>
        )}
      </div>

      {/* Latest subscribed + Top customers */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <LatestSubscribed items={latestSubscribed} />
          <TopCustomers     rows={topCustomers} />
        </div>
      )}

      {/* Charts */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <DailyVolumeChart     data={dailyData}  weeklyTotal={weeklyTotal} />
          <PlanDistributionChart data={planData}  total={planTotal} />
        </div>
      )}
    </div>
  );
};

export default AdminOverviewPage;
